import {
  BoundingSphere,
  Cartographic,
  Cesium3DTileFeature,
  defined,
  Model,
  SceneTransforms,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  Math as cesiumMath,
  Cartesian3,
  Entity,
  createGuid,
  Primitive,
  Color,
} from "@cesium/engine";
import {
  AnalysisVisibility,
  DitapAlert,
  EditUtils,
  LinestringEntity,
  MeasurementLengthHorizontal,
  MeasurementLengthVertical,
  MeasurementVolume,
  PointEntity,
  PolygonEntity,
  PopupAnalysisVisibility,
  PopupMeasurementVolume,
  PopupProperty,
  PopupStyleLinestring,
  PopupStylePoint,
  PopupStylePolygon,
  Utils,
} from "../../..";
import PopupMultiSelect from "../popup/PopupMultiSelect";
import PanelProperty from "../panel/PanelProperty";

/**
 * 객체 선택을 위한 사용자 입력(마우스/키보드) 클래스.</br>
 * User input (mouse/keyboard) class for object selection.
 * @alias InteractionSelect
 *
 * @constructor
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {object} selectOptions 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {Color} [selectOptions.hoverColor] 객체에 마우스를 이동할 때 변하는 색상. </br>The color that changes when the mouse moves over an object.
 * @param {Color} [selectOptions.selectedColor] 객체를 선택했을 때 변하는 색상. </br>The color that changes when an object is selected.
 * @param {object} propertyOptions 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {string} [propertyOptions.type] 속성 타입("popup" 또는 "panel").</br>The property Type("popup" or "panel").
 * @param {string} [propertyOptions.element] 속성 컨테이너 (요소 또는 아이디).</br>Specify a property container(id(string) or Element).
 * @param {Function} [propertyOptions.propertyCallback] 속성 팝업 생성 시 실행되는 콜백함수.<br>A promise that receives a selected object as an parameter and asynchronously requests property values.
 * @example
 * // 샌드캐슬 예제 'DiTAP - Model Select' 참조
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.978275264,
 *   37.566642192,
 *   400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *   destination: destination,
 * });
 *
 * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
 *   Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183)
 * );
 * const model = await Ditap.Model.fromGltfAsync{
 *  id: "model_01",
 *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
 *  modelMatrix: modelMatrix,
 *  scale: 3,
 * });
 * viewer.scene.primitives.add(model);
 *
 * const interactionSelect = new Ditap.InteractionSelect(viewer);
 * interactionSelect.on();
 *
 */
class InteractionSelect {
  constructor(viewer, selectOptions, propertyOptions) {
    selectOptions = this.checkSelectOptions(selectOptions);
    propertyOptions = this.checkPropertyOptions(propertyOptions);

    this._viewer = viewer;
    this._isOn = false;
    this._handler = undefined;
    this._selectedObject = undefined;
    this._moreButton = undefined;
    this._propertyButton = undefined;
    this._styleButton = undefined;
    this._voulumeButton = undefined;
    this._visibilityButton = undefined;
    this._isOnMoreButton = false;
    this._isOnMorePickerButton = false;
    this._removeCameraChangedEvent = undefined;
    this._multiSelectPopup = undefined;
    this._multiPopup = undefined;
    this._multiList = undefined;
    this._infoLabelEntity = undefined;
    this._windowEvent = {};
    this._mousePosition = undefined;
    this._buttonElement = undefined;
    this._hoverColor = selectOptions.hoverColor;
    this._selectedColor = selectOptions.selectedColor;
    this._propertyType = propertyOptions.type;
    this._propertyElement = propertyOptions.container;
    this._propertyCallback = propertyOptions.propertyCallback;
    this._highlightedObjects = [];
    this._isMobile = undefined;
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  set viewer(viewer) {
    this._viewer = viewer;
  }
  get viewer() {
    return this._viewer;
  }

  set isOn(isOn) {
    this._isOn = isOn;
  }
  /**
   * 핸들러 비/활성화.</br>
   * Check handler enable/disable status.
   * @readonly
   */
  get isOn() {
    return this._isOn;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /*
   * 선택된 객체.</br>
   * Selected object in DitapViewer.
   * @type {object}
   */
  set selectedObject(selectedObject) {
    if (
      this._selectedObject &&
      this.viewer.ditapStorage.getDitapEntity(this._selectedObject)
    ) {
      // 기존 선택된 다이탭 객체 displayCondition 복구
      const ditapEntity = this.viewer.ditapStorage.getDitapEntity(
        this._selectedObject
      );
      Utils.setDitapEntityDisplayCondition(
        ditapEntity,
        this._selectedDistanceDisplayCondition
      );
    }
    if (
      selectedObject &&
      this.viewer.ditapStorage.getDitapEntity(selectedObject)
    ) {
      // 새로 선택된 다이탭 객체 displayCondition 해제
      const ditapEntity = this.viewer.ditapStorage.getDitapEntity(
        selectedObject
      );
      this._selectedDistanceDisplayCondition = ditapEntity.displayCondition;
      Utils.setDitapEntityDisplayCondition(
        ditapEntity,
        Number.POSITIVE_INFINITY
      );
    }
    this._selectedObject = selectedObject;
  }
  get selectedObject() {
    return this._selectedObject;
  }

  /*
   * 더보기 버튼('...') 요소.</br>
   * The Element of more button('...').
   * @type {Element}
   */
  set moreButton(moreButton) {
    this._moreButton = moreButton;
  }
  get moreButton() {
    return this._moreButton;
  }

  /*
   * 속성 버튼 요소.</br>
   * The Element of property button.
   * @type {Element}
   */
  set propertyButton(propertyButton) {
    this._propertyButton = propertyButton;
  }
  get propertyButton() {
    return this._propertyButton;
  }

  /*
   * 스타일 버튼 요소.</br>
   * The Element of style button.
   * @type {Element}
   */
  set styleButton(styleButton) {
    this._styleButton = styleButton;
  }
  get styleButton() {
    return this._styleButton;
  }

  /*
   * 더보기 버튼 비/활성화.<br>
   * More button enabled or disabled.
   * @type {boolean}
   */
  set isOnMoreButton(isOnMoreButton) {
    this._isOnMoreButton = isOnMoreButton;
  }
  get isOnMoreButton() {
    return this._isOnMoreButton;
  }

  /*
   * '속성/스타일' 선택 버튼 비/활성화.<br>
   * 개체의 '속성,' '스타일,' '체적,' 및 '가시성'에 접근<br>
   * More picker button enabled or disabled.<br>
   * this button provides 'properties', 'style', 'volume', 'visibility' of object.
   * @type {boolean}
   */
  set isOnMorePickerButton(isOnMorePickerButton) {
    this._isOnMorePickerButton = isOnMorePickerButton;
  }
  get isOnMorePickerButton() {
    return this._isOnMorePickerButton;
  }

  /*
   * 카메라 시점 변경 이벤트 제거. </br>
   * Camera change event remover.
   * @type {Function}
   */
  set removeCameraChangedEvent(removeCameraChangedEvent) {
    this._removeCameraChangedEvent = removeCameraChangedEvent;
  }
  get removeCameraChangedEvent() {
    return this._removeCameraChangedEvent;
  }

  /*
   * MultiSelectPopup 요소.<br>
   * The Element of MultiSelectPopup.
   * @type {PopupMultiSelect}
   */
  set multiSelectPopup(multiSelectPopup) {
    this._multiSelectPopup = multiSelectPopup;
  }
  get multiSelectPopup() {
    return this._multiSelectPopup;
  }

  /*
   * 정보를 제공하는 라벨 요소.</br>
   * Label element providing information.
   * @type {Element}
   */
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }

  /*
   * 윈도우 이벤트를 포함하는 객체</br>
   * Objects containing Windows events.
   * @type {object[]}
   */
  set windowEvent(windowEvent) {
    this._windowEvent = windowEvent;
  }
  get windowEvent() {
    return this._windowEvent;
  }

  /*
   * 현재 마우스 좌표. </br>
   * Current mouse cartesian.
   * @type {Cartesian3}
   */
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }
  get mousePosition() {
    return this._mousePosition;
  }

  /*
   * 사용자 입력 클래스 활성화 버튼.</br>
   * Button that activates the user input class.
   * @type {Element}
   */
  set buttonElement(buttonElement) {
    this._buttonElement = buttonElement;
  }
  get buttonElement() {
    return this._buttonElement;
  }

  /*
   * 체적 버튼 요소.<br>
   * The Element of voulume button.
   * @type {Element}
   */
  set voulumeButton(voulumeButton) {
    this._voulumeButton = voulumeButton;
  }
  get voulumeButton() {
    return this._voulumeButton;
  }

  /*
   * 가시화 버튼 요소.<br>
   * The Element of visibility button.
   * @type {Element}
   */
  set visibilityButton(visibilityButton) {
    this._visibilityButton = visibilityButton;
  }
  get visibilityButton() {
    return this._visibilityButton;
  }

  /**
   * 마우스 호버시 변경될 색상.<br>
   * Color that changes when the mouse is over.
   * @type {Color}
   */
  set hoverColor(hoverColor) {
    this._hoverColor = hoverColor;
  }
  get hoverColor() {
    return this._hoverColor;
  }

  /**
   * 클릭시 변경될 색상.<br>
   * Color that changes when the mouse is clicked.
   * @type {Color}
   */
  set selectedColor(selectedColor) {
    this._selectedColor = selectedColor;
  }
  get selectedColor() {
    return this._selectedColor;
  }

  /**
   * 속성 패널 타입.("popup" or "panel"). <br>
   * Type that open property panel.
   * @type {string}
   */
  set propertyType(propertyType) {
    this._propertyType = propertyType;
  }
  get propertyType() {
    return this._propertyType;
  }

  /**
   * 속성 컨테이너 요소.<br>
   * Element of panel.
   * @type {Element}
   */
  set propertyElement(propertyElement) {
    this._propertyElement = propertyElement;
  }
  get propertyElement() {
    return this._propertyElement;
  }

  /**
   * 하이라이트 된 객체 배열.<br>
   * Array of highlighted objects.
   * @type {object[]}
   */
  set highlightedObjects(highlightedObjects) {
    this._highlightedObjects = highlightedObjects;
  }
  get highlightedObjects() {
    return this._highlightedObjects;
  }

  /**
   * 속성 팝업 생성시 실행되는 콜백함수.<br>
   * Callback function executed when creating property popup.
   * @type {Element}
   */
  get propertyCallback() {
    return this._propertyCallback;
  }
  set propertyCallback(propertyCallback) {
    if (
      propertyCallback instanceof Function ||
      propertyCallback instanceof undefined
    ) {
      this._propertyCallback = propertyCallback;
    }
  }

  /**
   * InteracionSelect on/off 기능.</br>
   * Function on and off.
   */
  toggle() {
    if (!this.isOn) {
      this.on();
    } else {
      this.off();
    }
  }

  /**
   * 객체 선택 핸들러 실행.</br>
   * Execute object selection handler.
   */
  on() {
    if (!this.isOn) {
      this.isOn = true;
      this._isMobile = Utils.detectDeviceType();
      this.handler = new ScreenSpaceEventHandler(this.viewer.canvas);
      this.percentageChanged = this.viewer.camera.percentageChanged;

      // 버튼 활성화
      this.buttonElement?.classList.add("on");

      // 핸들러 실행
      this.createLeftClickHandler();
      this.createRightClickHandler();

      // 더보기 버튼 생성
      this.createSelectButton();

      // 카메라 이동 이벤트 생성
      this.createCameraMoveEvent();

      // 더보기 버튼 클릭 이벤트 생성
      this.createMoreButtonEvent();

      // 속성 버튼 클릭 이벤트 생성
      this.createPropertyButtonEvent();

      // 스타일 버튼 클릭 이벤트 생성
      this.createStyleButtonEvent();

      // 체적 버튼 클릭 이벤트 생성
      this.createVolumeButtonEvent();

      // 가시성 버튼 클릭 이벤트 생성
      this.createVisibilityButtonEvent();

      // 다중 선택 박스 생성
      this.multiSelectPopup = new PopupMultiSelect(this, this.viewer);

      // 윈도우 이벤트 실행
      this.createWindowEvent();
      this.createResizeEvent();

      this.mobileInfoLabel = Utils.createMobileInfoLabel(
        this.viewer.container.querySelector(".cesium-viewer"),
        "선택할 대상을 클릭 또는 터치하세요"
      );
      this.infoLabel = Utils.createInfoLabel(
        this.viewer.container.querySelector(".cesium-viewer"),
        "선택할 대상을 클릭하세요"
      );

      if (this._isMobile) {
        this.cancelBtn = this.mobileInfoLabel.cancelBtn;
        this.infoLabelEntity = this.mobileInfoLabel.label;
        this.createMobileEvent();
        this.infoLabel.style.display = "none";
      } else {
        this.infoLabelEntity = this.infoLabel;
        this.createMouseMoveHandler();
        this.mobileInfoLabel.label.style.display = "none";
      }
    }
  }

  /**
   * 객체 선택 핸들러 종료.</br>
   * End object selection handler.
   */
  off() {
    if (this.isOn) {
      this.isOn = false;

      // 버튼 비활성화
      this.buttonElement?.classList.remove("on");

      // 도움말 라벨 제거.
      this.mobileInfoLabel.label.remove();
      this.infoLabel.remove();

      // 이벤트 제거
      if (this.handler) {
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
      }

      if (this._isMobile) {
        this.removeMobileEvent();
      }

      if (this.resizeEvent) {
        window.removeEventListener("resize", this.resizeEvent);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      // 더보기 버튼 삭제
      this.removeSelectButton();

      // 카메라 이동 이벤트 삭제
      this.removeCameraMoveEvent();

      // 윈도우 이벤트 삭제
      this.removeWindowEvent();

      // 스타일 팝업 삭제
      // const styleKeys = Object.keys(this.viewer.ditapStorage.stylePopups);
      // styleKeys.forEach((styleKey) => {
      //   this.viewer.ditapStorage.stylePopups[styleKey]?.destroy();
      // });

      if (this.selectedObject) {
        const originalColor = EditUtils.getOriginalColor(this.selectedObject);
        EditUtils.changeColor(this.selectedObject, originalColor);
      }

      this.highlightedObjects.filter((object) => {
        const originalColor = EditUtils.getOriginalColor(object);
        EditUtils.changeColor(object, originalColor);
      });

      // 다중선택 팝업 삭제
      this.multiSelectPopup.destroy();
      this.multiList = undefined;
      this.isOn = false;
      this.handler = undefined;
      this.selectedObject = undefined;
      this.moreButton = undefined;
      this.morePickerButton = undefined;
      this.isOnMoreButton = false;
      this.isOnMorePickerButton = false;
      this.removeCameraChangedEvent = undefined;
      this.multiSelectPopup = undefined;
      this._isMobile = undefined;
      this.infoLabelEntity = undefined;
      this.mobileInfoLabel = undefined;
      this.infoLabel = undefined;
    }
  }

  /**
   * 모바일 전용 이벤트 추가.<br>
   * Add mobile-specific event.
   */
  createMobileEvent() {
    this.cancelBtn.addEventListener(
      "click",
      (this.cancelEvent = () => {
        this.off();
      })
    );
  }

  /**
   * 모바일 전용 이벤트 제거.<br>
   * Remove mobile-specific event.
   */
  removeMobileEvent() {
    if (this.cancelEvent) {
      this.cancelBtn.removeEventListener("click", this.cancelEvent);
    }
    this.cancelEvent = undefined;
    this.cancelBtn = undefined;
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent() {
    const resizeOriginal = () => {
      if (this.isOn) {
        const isMobile = Utils.detectDeviceType();
        if (this._isMobile !== isMobile) {
          if (isMobile) {
            this._isMobile = true;

            this.cancelBtn = this.mobileInfoLabel.cancelBtn;
            this.infoLabelEntity = this.mobileInfoLabel.label;
            this.infoLabel.style.display = "none";
            this.mobileInfoLabel.label.style.display = "block";

            this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
            this.createMobileEvent();
          } else {
            this.removeMobileEvent();
            this._isMobile = false;
            this.infoLabelEntity = this.infoLabel;
            this.mobileInfoLabel.label.style.display = "none";
            this.infoLabel.style.display = "block";
            this.createMouseMoveHandler();
          }
        }

        this.handleButton(this.moreButton, this.isOnMoreButton);
        this.handleButton(this.morePickerButton, this.isOnMorePickerButton);
      }
    };

    this.resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.resizeEvent);

    const container = this.viewer.container;

    this.resizeObserver = new ResizeObserver(resizeOriginal);

    this.resizeObserver.observe(container);
  }

  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   */
  createLeftClickHandler() {
    // * 이벤트 핸들러 - LEFT_CLICK : 핸들러를 생성하는 함수
    const viewer = this.viewer;
    const handler = this.handler;

    // LEFT_CLICK 핸들러
    handler.setInputAction((e) => {
      if (this.isOn) {
        // 객체 선택
        const pickedObjects = viewer.scene.drillPick(
          e.position,
          undefined,
          1,
          1
        );

        // 선택한 기존 객체 색상 변경
        this.setSelectedObjectToOriginalColor();

        // 호버 객체 색상 변경
        this.highlightedObjects = EditUtils.setHighlightObjects(
          this.highlightedObjects,
          this.hoverColor,
          this.selectedObject,
          []
        );

        // 더보기 버튼 닫기
        this.closeMoreButton();

        // 속성 버튼 닫기
        this.closeMorePickerButton();

        // 다중선택 팝업 닫기
        this.multiSelectPopup.close();

        // 기존 판넬 닫기
        // this.closeProperty();

        this.selectedObject = undefined;

        // 첫번째 선택된 객체
        const firstPickedObject = pickedObjects[0];

        // 객체배열에서 중복 제외
        const uniquePickedObjects = this.getUniquePickedObjects(
          viewer,
          pickedObjects
        );

        if (uniquePickedObjects.length > 1) {
          // 1. 선택된 객체가 다중일 경우

          // 다중선택팝업에 객체 리스트 추가
          this.multiSelectPopup.addMultiList(uniquePickedObjects, undefined);
          this.multiSelectPopup.open();
        } else if (defined(firstPickedObject)) {
          // 2. 선택된 객체가 한개일 경우

          // 체적 계산 중인 경우
          if (
            this.viewer.ditapStorage.getDitapEntity(
              firstPickedObject
            ) instanceof MeasurementVolume &&
            this.viewer.ditapStorage.getDitapEntity(firstPickedObject).positions
              .length === 0
          ) {
            // 경고창 생성
            const warningAelrt = new DitapAlert({
              type: "warning",
              title: "경고",
              content1: "체적 계산 중인 경우 선택이 불가합니다.",
              buttons: "ONLYOK",
              open: false,
              callback: (alert) => {
                alert.destroy();
              },
              closeCallback: (alert) => {
                alert.destroy();
              },
            });
            warningAelrt.open();
            return;
          }

          // 선택한 객체 저장
          this.selectedObject = firstPickedObject;

          // 선택 객체 색상 변경
          this.highlightedObjects = this.highlightedObjects.filter(
            (obj) => obj !== firstPickedObject
          );

          const selectedColor = EditUtils.getColorByObjectType(
            this.selectedObject,
            this.selectedColor
          );

          EditUtils.setOriginalColor(this.selectedObject);

          EditUtils.changeColor(this.selectedObject, selectedColor);

          if (
            this.selectedObject instanceof Cesium3DTileFeature ||
            EditUtils.isCesium3DTileset(this.selectedObject)
          ) {
            // if (!this.selectedObject.getPropertyIds) {
            //   return;
            // }
            this.openProperty();
            return;
          }

          // 더보기 버튼 생성
          this.openMoreButton(firstPickedObject);

          // 선택한 객체로 카메라 줌
          EditUtils.zoomToObject(this.viewer, firstPickedObject);
        }
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   */
  createMouseMoveHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      const position = Utils.pickPosition(e.endPosition, this.viewer);

      if (defined(position)) {
        this.mousePosition = position;

        // 도움말 라벨 좌표 이동
        const entity_pos = SceneTransforms.wgs84ToWindowCoordinates(
          this.viewer.scene,
          this.mousePosition
        );

        this.infoLabelEntity.classList.remove("hide");
        this.infoLabelEntity.style.left = `${Number(entity_pos.x + 20)}px`;
        this.infoLabelEntity.style.top = `${Number(entity_pos.y + 20)}px`;
        this.infoLabelEntity.style.right = "unset";

        const objects = this.viewer.scene.drillPick(
          e.endPosition,
          undefined,
          1,
          1
        );

        // 색상 변경 함수
        this.highlightedObjects = EditUtils.setHighlightObjects(
          this.highlightedObjects,
          this.hoverColor,
          this.selectedObject,
          objects
        );
      } else {
        this.infoLabelEntity.classList.add("hide");
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /*
   * 선택된 객체 중 중복 제외한 객체 배열 조회.</br>
   * Get an array of objects excluding duplicates among the selected objects.
   * @param {object[]} objects 선택된 객체 배열.</br>Selected objects.
   * @returns {object[]} 중복 제외된 객체 배열.</br> Array of objects excluding duplicates.
   */
  getUniquePickedObjects(viewer, pickedObjects) {
    // * ENTITY - 선택된 다이탭 객체들중에서 중복을 제외한 객체 반환
    const pickedDitapObject = [];
    if (pickedObjects.length > 0) {
      pickedObjects.forEach((pickedObject) => {
        let object = pickedObject;
        if (viewer.ditapStorage.getDitapEntity(pickedObject)) {
          object = viewer.ditapStorage.getDitapEntity(pickedObject);
        }
        pickedDitapObject.push(object);
      });
    }
    // 중복을 제외한 SET 객체
    const set = new Set(pickedDitapObject);

    const uniquePickedObjects = [...set];

    return uniquePickedObjects;
  }

  /**
   * 선택된 객체 기존 색상으로 변경. <br>
   * change selected object to original color.
   */
  setSelectedObjectToOriginalColor() {
    // 선택한 기존 객체 색상 변경
    if (this.selectedObject) {
      const originalColor = EditUtils.getOriginalColor(this.selectedObject);
      EditUtils.changeColor(this.selectedObject, originalColor);
      this.selectedObject = undefined;
    }
  }

  /*
   * 더보기 버튼, '속성/스타일' 버튼 삭제.</br>
   * Removes element of button element.
   */
  removeSelectButton() {
    // * 버튼 - 더보기 버튼, '속성/스타일' 버튼을 삭제해주는 함수
    // 더보기 버튼 삭제
    if (this.moreButton instanceof HTMLElement) {
      this.moreButton.remove();
      this.moreButton = undefined;
    }

    // 속성/스타일 버튼 삭제
    if (this.morePickerButton instanceof HTMLElement) {
      this.morePickerButton.remove();
      this.morePickerButton = undefined;
    }
  }

  /*
   * 더보기 버튼, '속성/스타일' 버튼 생성.<br>
   * Create element of more button and more picker button.
   */
  createSelectButton() {
    // * 버튼 - 더보기 버튼, '속성/스타일' 버튼 생성 해주는 함수
    const container = this.viewer.container;
    const moreButton = document.createElement("button");
    const moreP = document.createElement("p");

    const attrDiv = document.createElement("div");
    const attrButton1 = document.createElement("button");
    const attrButton2 = document.createElement("button");
    const voulumeButton = document.createElement("button");
    const visibilityButton = document.createElement("button");

    moreButton.className = "ditap-btn-more hide";
    moreButton.classList.add("ditap-custom-button");
    moreP.className = "ditap-more-img";

    attrDiv.className = "ditap-btn-more ditap-btn-more-picker hide";
    attrButton1.className = "ditap-more-btn js-ditap-property-btn";
    attrButton2.className = "ditap-more-btn js-ditap-style-btn";
    voulumeButton.className = "ditap-more-btn js-ditap-volume-btn";
    visibilityButton.className = "ditap-more-btn js-ditap-visibility-btn";
    attrButton1.classList.add("ditap-custom-button");
    attrButton2.classList.add("ditap-custom-button");
    voulumeButton.classList.add("ditap-custom-button");
    visibilityButton.classList.add("ditap-custom-button");

    attrButton1.textContent = "속성";
    attrButton2.textContent = "스타일";
    voulumeButton.textContent = "체적";
    visibilityButton.textContent = "가시성";

    moreButton.appendChild(moreP);
    attrDiv.appendChild(attrButton1);
    attrDiv.appendChild(attrButton2);
    attrDiv.appendChild(voulumeButton);
    attrDiv.appendChild(visibilityButton);
    container.appendChild(moreButton);
    container.appendChild(attrDiv);

    this.moreButton = moreButton;

    this.morePickerButton = attrDiv;

    this.propertyButton = attrButton1;

    this.styleButton = attrButton2;

    this.voulumeButton = voulumeButton;
    this.visibilityButton = visibilityButton;
  }

  /*
   * 더보기 버튼에 이벤트 추가.<br>
   * Add click events to a more button Element.
   */
  createMoreButtonEvent() {
    // * 버튼 - 더보기 버튼에 이벤트 추가
    this.moreButton.addEventListener("click", () => {
      // 속성/스타일 버튼 OPEN
      this.openMorePickerButton(this.selectedObject);
    });
  }

  /*
   * 속성 버튼에 이벤트 추가.<br>
   * Add click events to a more picker button Element
   */
  createPropertyButtonEvent() {
    // * 속성 버튼 - 속성 버튼에 이벤트 추가
    this.propertyButton.addEventListener("click", () => {
      this.openProperty();
    });
  }

  openProperty() {
    if (
      EditUtils.isCesium3DTileset(this.selectedObject) &&
      !(this.selectedObject instanceof Cesium3DTileFeature)
    ) {
      const errorPropertyPopup = new DitapAlert({
        type: "warning",
        title: "속성 조회",
        content1: "피처가 존재하지 않습니다.",
        buttons: "ONLYOK",
        open: false,
        callback: (alert) => {
          alert.destroy();
        },
      });

      errorPropertyPopup.open();
    } else if (this.propertyType === "panel") {
      // 속성 판넬 OPEN
      this.openPropertyPanel(this.selectedObject);
    } else {
      // 속성 팝업 OPEN
      this.openPropertyPopup(this.selectedObject);
    }
  }

  closeProperty() {
    this.viewer.ditapPanelManager.closeOpenPanel();
  }

  /*
   * 스타일 버튼에 이벤트 추가.<br>
   * Add click events to a style button Element.
   */
  createStyleButtonEvent() {
    // * 스타일 버튼 - 스타일 버튼에 이벤트 추가
    this.styleButton.addEventListener("click", () => {
      // 스타일 팝업 OPEN
      this.openStylePopup(this.selectedObject);
    });
  }

  /*
   * 체적 버튼에 이벤트 추가.<br>
   * Add click events to a volume button Element.
   */
  createVolumeButtonEvent() {
    // * 체적 버튼 - 체적 버튼에 이벤트 추가
    this.voulumeButton.addEventListener("click", () => {
      // 체적 팝업 OPEN
      const volumeEntity = this.viewer.ditapStorage.getDitapEntity(
        this.selectedObject
      );

      if (volumeEntity && volumeEntity instanceof MeasurementVolume) {
        // 기존 팝업 삭제
        if (this.viewer.ditapStorage.volumePopups[volumeEntity.id]) {
          this.viewer.ditapStorage.volumePopups[volumeEntity.id]?.destroy();
        }

        this.viewer.ditapStorage.volumePopups[
          volumeEntity.id
        ] = new PopupMeasurementVolume(
          volumeEntity,
          this.viewer.ditapPaneManager
        );
        this.viewer.ditapStorage.volumePopups[volumeEntity.id].open();
      }

      // 더보기 버튼 비활성화
      this.closeMoreButton();

      // 속성/스타일 버튼 비활성화
      this.closeMorePickerButton();
    });
  }

  /*
   * 가시성 버튼에 이벤트 추가.<br>
   * Add click events to a visibility button Element.
   */
  createVisibilityButtonEvent() {
    // * 가시성 버튼 - 가시성 버튼에 이벤트 추가
    this.visibilityButton.addEventListener("click", () => {
      // 가시성 팝업 OPEN
      const visibilityEntity = this.viewer.ditapStorage.getDitapEntity(
        this.selectedObject
      );

      if (visibilityEntity && visibilityEntity instanceof AnalysisVisibility) {
        // 기존 팝업 삭제
        if (this.viewer.ditapStorage.visibilityPopups[visibilityEntity.id]) {
          this.viewer.ditapStorage.visibilityPopups[
            visibilityEntity.id
          ].destroy();
        }

        this.viewer.ditapStorage.visibilityPopups[
          visibilityEntity.id
        ] = new PopupAnalysisVisibility(
          visibilityEntity,
          this.viewer.ditapPaneManager
        );
        this.viewer.ditapStorage.visibilityPopups[visibilityEntity.id].open();
      }

      // 더보기 버튼 비활성화
      this.closeMoreButton();

      // 속성/스타일 버튼 비활성화
      this.closeMorePickerButton();
    });
  }
  /*
   * 선택한 엔터티의 중심 좌표 계산.<br>
   * Get the center position from selected entity.
   * @param {object} pickedObject 선택한 객체.<br>Selected object.
   * @returns {Cartesian2} 중심좌표.<br>Center windowPosition.
   */
  calculateCenter(pickedObject) {
    // * CARTESIAN3 - 3d 객체에 중간 지점을 계산해주는 함수
    let centerPosition;
    let windowPosition;
    const positions = EditUtils.getObjectCenterPosition(
      this.viewer,
      pickedObject
    );

    if (positions.length === 0) {
      return;
    }

    if (positions?.length === 1) {
      if (positions[0].getValue) {
        centerPosition = positions[0].getValue(new Date());
      } else {
        centerPosition = positions[0];
      }
    } else if (EditUtils.isPolyline(this.viewer, pickedObject)) {
      const ditapEntity = this.viewer.ditapStorage.getDitapEntity(pickedObject);
      if (ditapEntity instanceof MeasurementLengthVertical) {
        centerPosition = ditapEntity.verticalPosition;
      } else if (ditapEntity instanceof MeasurementLengthHorizontal) {
        centerPosition = ditapEntity.horizontalPosition;
      } else {
        centerPosition = this.calculateCenterPolylineFromPositions(positions);
      }
    } else if (EditUtils.isPolygon(this.viewer, pickedObject)) {
      centerPosition = this.calculateCenterPolygonFromPositions(positions);
    }

    if (centerPosition) {
      // cartesian -> window좌표로 변환 후 더보기 버튼 이동
      windowPosition = SceneTransforms.wgs84ToWindowCoordinates(
        this.viewer.scene,
        centerPosition
      );
    } else {
      console.error("InteractionSelect.js: Entity's cartesians not found");
    }

    return windowPosition;
  }

  /*
   * 좌표 배열의 중심 좌표 계산.<br>
   * Get the center position from Cartesian3 array.
   * @param {Cartesian3[]} positions 좌표 배열.<br>Cartesian3 array.
   * @returns {Cartesian3} 중심 좌표.<br>Center potision.
   */
  calculateCenterPolygonFromPositions(positions) {
    const cs = BoundingSphere.fromPoints(positions).center;
    const ctg = Cartographic.fromCartesian(cs);

    const lon = cesiumMath.toDegrees(ctg.longitude);
    const lat = cesiumMath.toDegrees(ctg.latitude);
    // const height = this.viewer.scene.globe.getHeight(ctg);
    const height = ctg.height;

    const centerPosition = Cartesian3.fromDegrees(lon, lat, height);

    return centerPosition;
  }

  /*
   * 폴리라인 엔티티의 중심 좌표 계산.<br>
   * Get the center position from polyline entity.
   * @param {Cartesian3[]} positions 폴리라인의 좌표 배열. <br>Cartesian3 array of Polyline.
   * @returns {Cartesian3} 중심 좌표.<br>Center potision.
   */
  calculateCenterPolylineFromPositions(positions) {
    let centerIndex; // 폴리라인 중심점 인덱스
    const cartesiansList = []; // lerp 배열
    for (let i = 0; i < positions.length - 1; i++) {
      const distance = Cartesian3.distance(positions[i], positions[i + 1]);
      let count;
      if (distance < 10) {
        //50cm 당 1
        count = Math.ceil(distance * 2);
      } else if (distance < 100) {
        //1m 당 1
        count = Math.ceil(distance);
      } else if (distance < 1000) {
        //10m 당 1
        count = Math.ceil(distance / 10);
      } else {
        //100m 당 1
        count = Math.ceil(distance / 100);
      }

      const cartesiansArray = new Array(count);
      for (let j = 0; j < count; ++j) {
        let offset;
        if (count <= 1) {
          offset = j;
        } else {
          offset = j / (count - 1);
        }
        cartesiansArray[j] = Cartesian3.lerp(
          positions[i],
          positions[i + 1],
          offset,
          new Cartesian3()
        );
      }
      cartesiansList.push(cartesiansArray);
    }

    const totalList = [];
    for (let i = 0; i < cartesiansList.length; i++) {
      for (let j = 0; j < cartesiansList[i].length; j++) {
        totalList.push(cartesiansList[i][j]);
      }
    }

    const arrayLength = totalList.length;

    if (arrayLength / 2 === 0) {
      centerIndex = arrayLength % 2;
    } else {
      centerIndex = Math.floor(arrayLength / 2);
    }

    return totalList[centerIndex];
  }

  /*
   * 카메라 시점 변경 이벤트 생성<br>
   * Add event when moving camera point of view.
   */
  createCameraMoveEvent() {
    // * 카메라 - 카메라 시점 변경 이벤트 생성 함수
    // 카메라 이동시 더보기 버튼 이동
    // 카메라 이동 이벤트 생성
    const removeCameraChangedEvent = this.viewer.camera.changed.addEventListener(
      () => {
        const moreButton = this.moreButton;
        const morePickerButton = this.morePickerButton;
        if (
          this.selectedObject &&
          this.selectedObject instanceof Cesium3DTileFeature
        ) {
          // 카메라 위치에 따른 cesium3dTileFeature레벨 변경 오류 해결
          try {
            const color = this.selectedObject.color;
            if (color) {
              console.log();
            }
          } catch {
            // 더보기 버튼 닫기
            this.closeMoreButton();

            // 속성 버튼 닫기
            this.closeMorePickerButton();

            // 다중선택 팝업 닫기
            this.multiSelectPopup.close();

            this.selectedObject = undefined;
          }
        }

        this.handleButton(moreButton, this.isOnMoreButton);
        this.handleButton(morePickerButton, this.isOnMorePickerButton);
      }
    );

    this.removeCameraChangedEvent = removeCameraChangedEvent;
  }

  // 선택된 객체가 존재하고 더보기 버튼이 있을 때 처리
  handleButton(button, isOnMoreButton) {
    if (this.viewer && button && isOnMoreButton && this.selectedObject) {
      const centerPosition = this.calculateCenter(this.selectedObject);
      if (centerPosition) {
        // ditapContainer의 width, height보다 큰 경우는 라벨 안보이게
        if (
          this.viewer.container.offsetWidth > centerPosition.x &&
          this.viewer.container.offsetHeight > centerPosition.y &&
          centerPosition.x > 0 &&
          centerPosition.y > 0
        ) {
          button.classList.remove("hide");
          button.style.left = `${centerPosition.x - button.clientWidth / 2}px`;
          button.style.top = `${centerPosition.y - button.clientHeight / 2}px`;
        } else {
          button.classList.add("hide");
        }
      }
    }
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   */
  createRightClickHandler() {
    const handler = this.handler;
    handler.setInputAction((e) => {
      this.off();
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /*
   * 카메라 시점 이동 이벤트 제거.<br>
   * Remove event when moving camera point of view.
   */
  removeCameraMoveEvent() {
    // 카메라 이동 감도 기존 값으로 변경
    this.viewer.camera.percentageChanged = this.percentageChanged;

    // 카메라 이동 이벤트 삭제
    this.removeCameraChangedEvent();
  }

  /*
   * 더보기 버튼 닫기.</br>
   * Hide more button element.
   */
  closeMoreButton() {
    // * 버튼 - 더보기 버튼 CLOSE
    this.moreButton.classList.add("hide");

    // 더보기 버튼 활성화 여부 저장
    this.isOnMoreButton = false;
  }

  /*
   * '속성/스타일' 버튼 닫기.</br>
   * Hide more picker button element.
   */
  closeMorePickerButton() {
    //  * 버튼 - 더보기 버튼 CLOSE
    this.morePickerButton.classList.add("hide");

    // 속성 버튼 활성화 여부 저장
    this.isOnMorePickerButton = false;
  }

  /*
   * 더보기 버튼 열기.</br>
   * Open more button element.
   * @param {object} object 선택된 객체.<br>selected object.
   */
  openMoreButton(pickedObject) {
    // * 버튼 - 더보기 버튼 OPEN
    // 더보기 버튼 활성화 여부 저장
    this.isOnMoreButton = true;

    // 더보기 버튼 활성화
    this.moreButton.classList.remove("hide");

    // 속성/스타일 버튼 비활성화
    this.closeMorePickerButton();

    this.handleButton(this.moreButton, this.isOnMoreButton);
  }

  /*
   * '속성/스타일' 버튼 열기.</br>
   * Open more button element.
   * @param {object} object 선택된 객체.<br>selected object.
   */
  openMorePickerButton(pickedObject) {
    // 더보기 버튼 비활성화
    this.closeMoreButton();

    // 속성/스타일 버튼 활성화 여부 초기화
    this.morePickerButton.classList.remove("hide");
    this.isOnMorePickerButton = true;

    // 뷰어에 저장된 엔티티면 버튼 활성화
    const ditapEntity = this.viewer.ditapStorage.getDitapEntity(pickedObject);
    let displayStyle = "none";
    let volumeStyle = "none";
    let visibilityStyle = "none";

    if (ditapEntity) {
      // 점, 선, 면인 경우 -> 스타일 팝업 클릭
      if (
        ditapEntity instanceof PointEntity ||
        ditapEntity instanceof LinestringEntity ||
        ditapEntity instanceof PolygonEntity
      ) {
        displayStyle = "block";
      } else if (ditapEntity instanceof MeasurementVolume) {
        volumeStyle = "block";
      } else if (ditapEntity instanceof AnalysisVisibility) {
        visibilityStyle = "block";
      }
    }
    // 버튼 스타일 설정
    this.styleButton.style.display = displayStyle;
    this.voulumeButton.style.display = volumeStyle;
    this.visibilityButton.style.display = visibilityStyle;

    this.handleButton(this.morePickerButton, this.isOnMorePickerButton);
  }

  /**
   * 속성 판넬 활성화.<br>
   * Open property panel.
   * @param {object} object 선택된 객체.<br> Selected object.
   */
  openPropertyPanel(pickedObject) {
    // 더보기 버튼 비활성화
    this.closeMoreButton();

    // 속성/스타일 버튼 활성화
    this.closeMorePickerButton();

    const selectedObjectId = EditUtils.getSelectObjectId(
      this.viewer,
      pickedObject
    );
    const panelManager = this.viewer.ditapPanelManager;

    // 기존 패널이 있는지 확인
    const oldPropertyPanel = panelManager.getPanelBySelectedObjectId(
      selectedObjectId
    );

    if (
      panelManager.isPanelsById(selectedObjectId) &&
      oldPropertyPanel instanceof PanelProperty
    ) {
      // PointEntity, LineStringEntity, PolygonEntity일 경우 position 업데이트
      const ditapEntity = this.viewer.ditapStorage.getDitapEntity(pickedObject);
      if (
        ditapEntity instanceof PointEntity ||
        ditapEntity instanceof LinestringEntity ||
        ditapEntity instanceof PolygonEntity
      ) {
        const newPositions = this.calcCartesiansToPositionsString(ditapEntity);
        oldPropertyPanel.updatePositions(newPositions);
      }

      // 기존 패널이 있으면 열기
      oldPropertyPanel.open();
    } else {
      // 선택한 패널 추가
      const selectedObjectProperties = {
        title: "selected feature",
        properties: this.getSelectObjectProperty(pickedObject),
      };

      // const closeCallback = () => {
      if (pickedObject === this.selectedObject) {
        this.setSelectedObjectToOriginalColor();
      }
      // };
      this.viewer.ditapPanelManager.addPanel(
        new PanelProperty({
          container: this.propertyElement,
          viewer: this.viewer,
          selectedObject: pickedObject,
          properties: [selectedObjectProperties],
          propertyCallback: this.propertyCallback,
          // closeCallback: closeCallback,
        })
      );
    }
  }

  calcCartesiansToPositionsString(pickedObject) {
    const positions = EditUtils.getObjectPositions(this.viewer, pickedObject);

    const cartographicString = positions
      .map((cartesian) => {
        const cartographic = Cartographic.fromCartesian(cartesian);
        if (!cartographic) {
          return "undefined";
        }
        const longitude = cesiumMath.toDegrees(cartographic?.longitude);
        const latitude = cesiumMath.toDegrees(cartographic?.latitude);
        const altitude = cartographic?.height;
        return `(${longitude}, ${latitude}, ${altitude})`;
      })
      .join(", ");

    return cartographicString;
  }

  /**
   * 선택된 객체의 속성을 조회.<br>
   * Get DitapEntity Saved in DitapViewer Storage.
   * @param {object} pickedObject 선택된 객체.<br> The selected object.
   * @returns {object} 선택된 객체의 속성.<br>The properties of selected Object.
   */
  getSelectObjectProperty(pickedObject) {
    // * OBJECT - 선택된 객체의 모든 속성을 리턴하는 함수
    // 뷰어에 저장된 엔티티
    const ditapEntity = this.viewer.ditapStorage.getDitapEntity(pickedObject);

    const property = {};

    // POSITIONS 추가
    property["POSITIONS"] = this.calcCartesiansToPositionsString(pickedObject);

    if (ditapEntity) {
      // 뷰어에 저장된 엔티티인 경우
      property["ID"] = ditapEntity.id;

      if (ditapEntity instanceof LinestringEntity) {
        // 스타일
        const polyline = ditapEntity.polylineEntity.polyline;

        if (polyline && polyline.show.getValue()) {
          const style = ditapEntity.getMaterialStyle();
          property["STYLE"] = style.toUpperCase();
        }
      }
    } else if (
      EditUtils.isCesium3DTileset(pickedObject) ||
      pickedObject instanceof Cesium3DTileFeature
    ) {
      let tilset = pickedObject;
      if (!(pickedObject instanceof Cesium3DTileFeature)) {
        tilset = EditUtils.getCesium3DTileset(pickedObject);
      }
      // 3d 타일인 경우
      const tileProperty = EditUtils.get3DTileProperty(tilset);
      if (tileProperty) {
        Object.assign(property, tileProperty);
      }
    } else if (
      pickedObject.primitive instanceof Model ||
      pickedObject.primitive instanceof Primitive
    ) {
      // Model인 경우
      const entityId =
        pickedObject.id instanceof Entity
          ? pickedObject.id.id ?? "-"
          : pickedObject.id ?? "-";
      Object.assign(property, {
        ID: entityId,
      });
      if (pickedObject.name) {
        Object.assign(property, {
          NAME: pickedObject.name ?? undefined,
        });
      }
    } else if (pickedObject.id instanceof Entity) {
      // ENTITY
      const entityId = pickedObject.id.id ? pickedObject.id.id : createGuid();
      Object.assign(property, { ID: entityId });
      if (pickedObject.id.name) {
        Object.assign(property, {
          NAME: pickedObject.id.name ?? undefined,
        });
      }

      if (pickedObject.id.properties) {
        Object.assign(
          property,
          pickedObject.id.properties.getValue(new Date())
        );
      }
      if (!pickedObject.id.id) {
        pickedObject.id.id = entityId; // ID가 없는 경우 생성된 ID를 할당
      }
    }

    return property;
  }

  /*
   * 속성 팝업 열기.</br>
  Open property popup.
   * @param {object} object 선택된 객체.<br>selected object.
   */
  openPropertyPopup(pickedObject) {
    // 더보기 버튼 비활성화
    this.closeMoreButton();

    // 속성/스타일 버튼 활성화
    this.closeMorePickerButton();

    const selectedObjectId = EditUtils.getSelectObjectId(
      this.viewer,
      pickedObject
    );
    if (this.viewer.ditapStorage.propertyPopups[selectedObjectId]) {
      // 기존 팝업 삭제
      const oldPropertyPopup = this.viewer.ditapStorage.propertyPopups[
        selectedObjectId
      ];
      oldPropertyPopup.destroy();
    }

    // 선택한 객체의 속성 조회

    const selectedObjectProperties = {
      title: "selected feature",
      properties: this.getSelectObjectProperty(pickedObject),
    };

    // const closeCallback = () => {
    if (pickedObject === this.selectedObject) {
      this.setSelectedObjectToOriginalColor();
    }
    // };
    this.viewer.ditapStorage.propertyPopups[
      selectedObjectId
    ] = new PopupProperty({
      viewer: this.viewer,
      selectedObject: pickedObject,
      properties: [selectedObjectProperties],
      propertyCallback: this.propertyCallback,
      // closeCallback: closeCallback,
    });
  }

  /*
   * 스타일 팝업 열기.</br>Open style popup.
   * @param {object} object 선택된 객체.<br>selected object.
   */
  openStylePopup(pickedObject) {
    // 더보기 버튼 및 속성/스타일 버튼 비활성화
    this.closeMoreButton();
    this.closeMorePickerButton();

    // 스타일 팝업 생성
    const ditapEntity = this.viewer.ditapStorage.getDitapEntity(pickedObject);
    if (!ditapEntity) {
      return;
    }

    const selectedObjectId = EditUtils.getSelectObjectId(
      this.viewer,
      pickedObject
    );
    const { stylePopups } = this.viewer.ditapStorage;

    // 이미 같은 이름의 객체의 팝업이 있는 경우 삭제
    const oldStylePopup = stylePopups[selectedObjectId];
    if (oldStylePopup) {
      oldStylePopup.destroy();
    }

    // 스타일 팝업 생성
    let newStylePopup;
    if (ditapEntity instanceof PointEntity) {
      newStylePopup = new PopupStylePoint(
        ditapEntity,
        this.viewer.ditapPaneManager
      );
    } else if (ditapEntity instanceof LinestringEntity) {
      newStylePopup = new PopupStyleLinestring(
        ditapEntity,
        this.viewer.ditapPaneManager
      );
    } else if (ditapEntity instanceof PolygonEntity) {
      newStylePopup = new PopupStylePolygon(
        ditapEntity,
        this.viewer.ditapPaneManager
      );
    }

    if (newStylePopup) {
      stylePopups[selectedObjectId] = newStylePopup;
    }
  }

  /*
   * 윈도우 객체에 이벤트 추가.</br>
   * Add events to Window object
   */
  createWindowEvent() {
    // * 이벤트 - window 이벤트 생성함수
    // keyDown 이벤트 생성
    window.addEventListener(
      "keydown",
      (this.windowEvent["keydown"] = (e) => {
        this.terminate(e);
      })
    );
  }

  /*
   * Excape 키 다운 시 실행 이벤트.</br>
   * Exit a handler for deleting objects.
   * @param {Event} event 키보드 이벤트.</br> Keyboard event.
   */
  terminate(event) {
    if (event.key === "Escape") {
      this.off();
    }
  }

  /*
   * 윈도우 객체에 추가된 이벤트 제거.</br>
   * Remove Events added on Windows Objects.
   */
  removeWindowEvent() {
    const keys = Object.keys(this.windowEvent);
    for (let i = 0; i < keys.length; i++) {
      window.removeEventListener(keys[i], this.windowEvent[keys[i]]);
    }
  }

  /**
   *  선택 옵션 확인.<br>
   *  check the object of select.
   * @param {object} options 선택 옵션.<br>object of select options.
   * @returns {object} 확인된 선택 옵션.<br>Verified select options.
   */
  checkSelectOptions(options) {
    const newOptions = {};

    function getColorOrDefault(color, defaultColor) {
      return color instanceof Color
        ? color
        : Color.fromCssColorString(defaultColor);
    }

    if (typeof options === "object") {
      newOptions.hoverColor = getColorOrDefault(options.hoverColor, "#BDBDBD");
      newOptions.selectedColor = getColorOrDefault(
        options.selectedColor,
        "#9DCFFF"
      );
    } else {
      newOptions.hoverColor = Color.fromCssColorString("#BDBDBD");
      newOptions.selectedColor = Color.fromCssColorString("#9DCFFF");
    }

    return newOptions;
  }

  /**
   * 속성 타입 확인.<br>
   * check the property type.
   * @param {object} property 속성 옵션.<br>object of property.
   * @returns {object} 확인된 속성 옵션.<br>Verified property type.
   */
  checkPropertyOptions(property) {
    const newProperty = {};

    // 1. property 객체가 없으면 default(popup)
    if (!property) {
      return (newProperty["type"] = "popup");
    }

    // 2. property 객체가 있으면 해당 타입으로 설정
    newProperty["type"] =
      property["type"]?.toLowerCase().replace(/(\s*)/g, "") === "panel"
        ? "panel"
        : "popup";

    // 3. property 내부에 element 확인하여 할당
    if (property.type === "panel") {
      const element = property["container"];
      if (element instanceof HTMLDivElement) {
        // 엘리먼트일 경우
        newProperty["container"] = element;
      } else if (typeof element === "string") {
        // 문자열일 경우
        const elementById = document.getElementById(element);
        newProperty["container"] = elementById;
      } else {
        throw Error(
          "DitapViewer: Setting the property options, 'container' is required."
        );
      }
    }

    // propertyCallback 확인하여 할당
    if (
      property.propertyCallback instanceof Function ||
      property.propertyCallback === undefined
    ) {
      newProperty["propertyCallback"] = property.propertyCallback;
    } else {
      console.error("InteractionSelect propertyCallback type error");
    }

    return newProperty;
  }
  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object.
   */
  destroy() {
    this.off();
  }
}
export default InteractionSelect;
